import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { deleteProgram, hydrateProgramCatalog, loadPrograms } from '../../actions/programs';
import { ProcessProgramResult, processProgram } from '../../actions/workouts';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import { ConfirmationDialog } from '../common/Modal';
import DeleteProgramDialog from './DeleteProgramDialog';
import ProgramListTable from './ProgramListTable';
import ProcessProgramDialog from './ProcessProgramDialog';
import { ProgramManagementListItem } from './programs.types';

interface ComponentProps {}

interface ReduxProps {
    deletingProgramName?: string;
    hydratingProgramCatalog: boolean;
    isAdmin: boolean;
    items: ProgramManagementListItem[];
    loadingList: boolean;
    processingProgram: boolean;
}

interface ReduxActionProps {
    deleteProgram: (name: string) => Promise<{ message?: string; success: boolean }>;
    hydrateProgramCatalog: (overwriteExisting?: boolean) => Promise<{ message?: string; success: boolean }>;
    loadPrograms: () => void;
    processProgram: (programKey: string) => Promise<ProcessProgramResult>;
}

interface State {
    hydrateOverwriteExisting: boolean;
    hydrateDialogVisible: boolean;
    pendingDeleteProgramName?: string;
    pendingProcessProgramName?: string;
    searchTerm: string;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class ProgramManagementPage extends Component<Props, State> {
    state: State = {
        hydrateOverwriteExisting: false,
        hydrateDialogVisible: false,
        searchTerm: '',
    };

    componentDidMount() {
        this.props.loadPrograms();
    }

    getFilteredItems() {
        const normalizedSearchTerm = this.state.searchTerm.trim().toLowerCase();

        if (!normalizedSearchTerm) {
            return this.props.items;
        }

        return this.props.items.filter((item) => {
            return [
                item.name,
                item.displayName,
                item.description,
                item.nextProgramKey || '',
            ].some((value) => value.toLowerCase().includes(normalizedSearchTerm));
        });
    }

    getPendingDeleteItem() {
        return this.props.items.find((item) => item.name === this.state.pendingDeleteProgramName);
    }

    getPendingProcessItem() {
        return this.props.items.find((item) => item.name === this.state.pendingProcessProgramName);
    }

    onCloseDeleteDialog = () => {
        this.setState({ pendingDeleteProgramName: undefined });
    };

    onConfirmDelete = async () => {
        const { pendingDeleteProgramName } = this.state;

        if (!pendingDeleteProgramName) {
            return;
        }

        const result = await this.props.deleteProgram(pendingDeleteProgramName);

        if (result.success) {
            this.setState({ pendingDeleteProgramName: undefined });
        }
    };

    onCloseProcessDialog = () => {
        if (this.props.processingProgram) {
            return;
        }

        this.setState({ pendingProcessProgramName: undefined });
    };

    onConfirmProcess = async () => {
        const pendingProcessItem = this.getPendingProcessItem();

        if (!pendingProcessItem) {
            return;
        }

        const result = await this.props.processProgram(pendingProcessItem.name);

        if (result.success) {
            this.setState({ pendingProcessProgramName: undefined });
        }
    };

    onCreateProgram = () => {
        this.props.navigate('/programs/new');
    };

    onOpenHydrateDialog = () => {
        this.setState({ hydrateDialogVisible: true, hydrateOverwriteExisting: false });
    };

    onCloseHydrateDialog = () => {
        if (this.props.hydratingProgramCatalog) {
            return;
        }

        this.setState({ hydrateDialogVisible: false, hydrateOverwriteExisting: false });
    };

    onHydrateOverwriteExistingChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ hydrateOverwriteExisting: event.target.checked });
    };

    onConfirmHydrate = async () => {
        const result = await this.props.hydrateProgramCatalog(this.state.hydrateOverwriteExisting);

        if (result.success) {
            this.setState({ hydrateDialogVisible: false, hydrateOverwriteExisting: false });
        }
    };

    onImportProgram = () => {
        this.props.navigate('/programs/new?import=1');
    };

    onEditProgram = (name: string) => {
        this.props.navigate(`/programs/${name}/edit`);
    };

    onOpenDeleteDialog = (name: string) => {
        this.setState({ pendingDeleteProgramName: name });
    };

    onOpenProcessDialog = (name: string) => {
        this.setState({ pendingProcessProgramName: name });
    };

    onOpenProgram = (name: string) => {
        this.props.navigate(`/programs/${name}`);
    };

    onSearchChanged = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchTerm: event.target.value });
    };

    render() {
        const { deletingProgramName, hydratingProgramCatalog, isAdmin, items, loadingList, processingProgram } = this.props;
        const filteredItems = this.getFilteredItems();
        const pendingDeleteItem = this.getPendingDeleteItem();
        const pendingProcessItem = this.getPendingProcessItem();
        const fullyConfiguredCount = items.filter((item) => item.status === 'Complete').length;

        if (loadingList && !items.length) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Box maxWidth={1280}>
                <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: { md: '1.6rem', xs: '1.4rem' }, fontWeight: 700, mb: 0.25 }} variant="h4">Programs</Typography>
                        <Typography color="text.secondary" sx={{ maxWidth: 760 }} variant="body1">
                            Build, review, and maintain week-by-week program schedules.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1 }}>
                        {isAdmin && (
                            <Button
                                disabled={hydratingProgramCatalog}
                                onClick={this.onOpenHydrateDialog}
                                startIcon={<AutorenewRoundedIcon />}
                                variant="outlined"
                            >
                                {hydratingProgramCatalog ? 'Hydrating...' : 'Hydrate Catalog'}
                            </Button>
                        )}
                        <Button onClick={this.onImportProgram} startIcon={<UploadFileRoundedIcon />} variant="outlined">Import CSV/TSV</Button>
                        <Button onClick={this.onCreateProgram} startIcon={<AddRoundedIcon />} variant="contained">Create Program</Button>
                    </Box>
                </Box>
                <Paper sx={{ borderRadius: 3, mb: 2.5, p: 2 }} variant="outlined">
                    <TextField fullWidth label="Search Programs" onChange={this.onSearchChanged} size="small" value={this.state.searchTerm} />
                </Paper>
                {!filteredItems.length && (
                    <Paper sx={{ borderRadius: 3, p: 5, textAlign: 'center' }} variant="outlined">
                        <Typography sx={{ fontWeight: 700, mb: 1 }} variant="h6">No Programs Yet</Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                            Create your first program to start building week-by-week workout schedules.
                        </Typography>
                        <Button onClick={this.onCreateProgram} startIcon={<AddRoundedIcon />} variant="contained">Create First Program</Button>
                    </Paper>
                )}
                {filteredItems.length > 0 && (
                    <ProgramListTable
                        deletingProgramName={deletingProgramName}
                        items={filteredItems}
                        onDelete={this.onOpenDeleteDialog}
                        onEdit={this.onEditProgram}
                        onOpen={this.onOpenProgram}
                        onProcess={this.onOpenProcessDialog}
                        processingProgram={processingProgram}
                        processingProgramName={processingProgram ? pendingProcessItem?.name : undefined}
                    />
                )}
                <Paper sx={{ borderRadius: 3, mt: 2.5, p: 2 }} variant="outlined">
                    <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle1">Summary</Typography>
                    <Typography variant="body2">Total Programs: {items.length}</Typography>
                    <Typography variant="body2">Fully Configured: {fullyConfiguredCount}</Typography>
                    <Typography variant="body2">With Gaps: {Math.max(items.length - fullyConfiguredCount, 0)}</Typography>
                </Paper>
                <DeleteProgramDialog
                    deleting={deletingProgramName === pendingDeleteItem?.name}
                    name={pendingDeleteItem?.name}
                    onCancel={this.onCloseDeleteDialog}
                    onConfirm={this.onConfirmDelete}
                    totalDays={pendingDeleteItem?.totalDays}
                    totalWeeks={pendingDeleteItem?.totalWeeks}
                    visible={Boolean(pendingDeleteItem)}
                />
                <ProcessProgramDialog
                    displayName={pendingProcessItem?.displayName}
                    name={pendingProcessItem?.name}
                    onCancel={this.onCloseProcessDialog}
                    onConfirm={this.onConfirmProcess}
                    processing={processingProgram}
                    totalDays={pendingProcessItem?.totalDays}
                    totalWeeks={pendingProcessItem?.totalWeeks}
                    visibility={pendingProcessItem?.visibility}
                    visible={Boolean(pendingProcessItem)}
                />
                <ConfirmationDialog
                    cancelButtonText="Cancel"
                    id="hydrate-program-catalog"
                    okButtonText={hydratingProgramCatalog ? 'Hydrating...' : 'Hydrate Catalog'}
                    onClose={this.onCloseHydrateDialog}
                    onConfirmed={this.onConfirmHydrate}
                    title="Hydrate Program Catalog"
                    visible={this.state.hydrateDialogVisible}
                >
                    <Typography sx={{ mb: 1.5 }} variant="body2">
                        This will recreate missing program catalog entries from the current programs data.
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        Existing catalog metadata will be preserved unless the API needs to repair missing core fields such as the stored program key.
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={this.state.hydrateOverwriteExisting} onChange={this.onHydrateOverwriteExistingChanged} />}
                        label="Fully overwrite existing program catalog metadata"
                        sx={{ mt: 1.5 }}
                    />
                    {this.state.hydrateOverwriteExisting && (
                        <Typography color="error" variant="body2">
                            Full overwrite will replace existing catalog metadata such as display name, visibility, and weeks using values inferred from the programs data.
                        </Typography>
                    )}
                </ConfirmationDialog>
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ auth, misc, programs }: ReduxState) => ({
    deletingProgramName: programs.deletingProgramName,
    hydratingProgramCatalog: programs.hydratingProgramCatalog,
    isAdmin: Boolean(auth.user?.admin),
    items: programs.items,
    loadingList: programs.loadingList,
    processingProgram: misc.processingProgram,
});

const mapDispatchToProps: any = {
    deleteProgram,
    hydrateProgramCatalog,
    loadPrograms,
    processProgram,
};

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, mapDispatchToProps)(withRouter(ProgramManagementPage));